import { useSyncExternalStore } from 'react';
import { Link, useLocation } from 'react-router';
import { COMPANY, SITE, copyrightNotice } from '@/content/company';
import { LEGAL_NAV, PRIMARY_NAV } from '@/content/routes';
import { LogoMark } from './Logo';
import styles from './Footer.module.css';

/*
 * The year printed in the copyright notice.
 *
 * Calling `new Date().getFullYear()` straight from render looks obvious and is
 * wrong: it runs once during the build-time prerender and again during
 * hydration in the visitor's browser. Those agree for eleven months and then
 * differ on 1 January for any deploy older than that - a hydration mismatch
 * that surfaces only on a public holiday.
 *
 * `useSyncExternalStore` exists for precisely this. React renders
 * `getServerSnapshot` during hydration, so the client's first paint matches the
 * prerendered HTML exactly, then re-renders with `getSnapshot`. The result is
 * static HTML carrying the build year and a live page carrying the real one,
 * with no mismatch in between - which is the only way a static site stays right
 * about the year without being redeployed.
 *
 * `subscribe` is a no-op: the year does not change during a session, and in the
 * once-a-year case where a tab is open across midnight, a stale footer is not
 * worth an interval timer. It lives at module scope so the reference is stable
 * and React does not resubscribe on every render.
 */
const noopSubscribe = () => {
  // Nothing to subscribe to; the unsubscribe is likewise a no-op.
  return () => undefined;
};
const currentYear = () => new Date().getFullYear();
const buildYear = () => __BUILD_YEAR__;

function useCurrentYear(): number {
  return useSyncExternalStore(noopSubscribe, currentYear, buildYear);
}

/**
 * Site footer.
 *
 * The design's column headings were styled `div`s; they head genuine groups of
 * links, so they are `h2`s here and the two link groups are labelled `nav`
 * landmarks. Nothing about the rendering changes.
 */
export function Footer() {
  const { pathname } = useLocation();
  const isCurrent = (path: string) => (pathname === path ? 'page' : undefined);
  const year = useCurrentYear();

  return (
    <footer className={styles.footer}>
      <div className={styles.columns}>
        <div className={styles.brandColumn}>
          <Link to="/" className={`${styles.brand} sk-hit`}>
            <LogoMark size={18} />
            <span className={styles.brandName}>Skabro</span>
          </Link>
          <p className={styles.blurb}>{SITE.tagline}</p>
          <div className={styles.email}>
            <a href={`mailto:${COMPANY.email}`} className="sk-hit">
              {COMPANY.email}
            </a>
          </div>
        </div>

        <nav aria-labelledby="footer-site-heading">
          <h2 id="footer-site-heading" className={styles.heading}>
            Site
          </h2>
          <div className={styles.links}>
            {PRIMARY_NAV.map((route) => (
              <Link
                key={route.path}
                to={route.path}
                className={styles.link}
                aria-current={isCurrent(route.path)}
              >
                {route.label}
              </Link>
            ))}
          </div>
        </nav>

        <nav aria-labelledby="footer-legal-heading">
          <h2 id="footer-legal-heading" className={styles.heading}>
            Legal
          </h2>
          <div className={styles.links}>
            {LEGAL_NAV.map((route) => (
              <Link
                key={route.path}
                to={route.path}
                className={styles.link}
                aria-current={isCurrent(route.path)}
              >
                {route.label}
              </Link>
            ))}
          </div>
        </nav>

        <div className={styles.addressColumn}>
          <h2 className={styles.heading}>{COMPANY.legalName}</h2>
          <address className={styles.address}>
            {COMPANY.address.street}
            <br />
            {COMPANY.address.locality}
            <br />
            {COMPANY.address.country}
          </address>
          <div className={styles.registration}>
            IČO {COMPANY.ico} · IČ DPH {COMPANY.icDph}
            <br />
            {COMPANY.register.short}
          </div>
        </div>
      </div>

      <div className={styles.bar}>
        <span>
          © {copyrightNotice(year)} {COMPANY.legalName}
        </span>
        <span>Registered in the Slovak Republic</span>
      </div>
    </footer>
  );
}
